// Player de preview de voz COMPARTILHADO (um único <audio>) + estado "tocando
// agora", usado pela página de TTS E pelo modal Biblioteca de Vozes. Alimenta a
// barra inferior estilo ElevenLabs (VoicePreviewBar). Antes a página e o modal
// tinham players separados/dessincronizados; aqui há UM só.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getPreviewUrl, uniqueLanguages } from '../utils/voiceMetadata';

const Ctx = createContext(null);

const NOOP = {
  voice: null, langKey: null, play: () => {}, setLanguage: () => {}, stop: () => {},
  isVoicePlaying: () => false, activeKeyFor: () => null, nowPlaying: null,
  showExternal: () => {}, updateExternal: () => {}, clearExternal: () => {},
  barActive: false, barKind: 'preview', barVoice: null, barLangKey: null, barName: '',
  barCurrentUrl: null, barIsPlaying: false, barCurrentTime: 0, barDuration: 0,
  barToggle: () => {}, barSeek: () => {}, barSkip: () => {}, barStop: () => {},
};

export const useVoicePreview = () => useContext(Ctx) || NOOP;

const keyOf = (language, accent) => `${language || ''}|${accent || ''}`;

// Idiomas com preview de uma voz (deduplicados), para o seletor de idioma.
export function previewLanguagesOf(voice) {
  const vl = voice && voice.voice_info && voice.voice_info.verified_languages;
  if (!Array.isArray(vl)) return [];
  return uniqueLanguages(vl).filter((e) => e && e.preview_url);
}

export function VoicePreviewProvider({ children }) {
  const audioRef = useRef(null);
  const [voice, setVoice] = useState(null);
  const [langKey, setLangKey] = useState(null);
  const [currentUrl, setCurrentUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // Modo EXTERNO (áudio de RESULTADO): a barra exibe/controla um player de fora
  // (o <audio> da página), delegando os controles. Mantém o player de resultado
  // intacto; só espelha o estado na barra.
  const [ext, setExt] = useState(null); // { name, url, isPlaying, currentTime, duration }
  const extCtl = useRef({});

  const teardown = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.onended = null; a.onerror = null; a.ontimeupdate = null; a.onloadedmetadata = null;
    }
    audioRef.current = null;
  }, []);

  const stop = useCallback(() => {
    teardown();
    setVoice(null); setLangKey(null); setCurrentUrl(null);
    setIsPlaying(false); setCurrentTime(0); setDuration(0);
  }, [teardown]);

  const start = useCallback((vc, key, url) => {
    // Exclusividade: ao tocar um preview, para/limpa o player externo (resultado).
    if (extCtl.current && extCtl.current.stop) extCtl.current.stop();
    extCtl.current = {};
    setExt(null);
    teardown();
    const a = new Audio(url);
    audioRef.current = a;
    a.onloadedmetadata = () => setDuration(Number.isFinite(a.duration) ? a.duration : 0);
    a.ontimeupdate = () => setCurrentTime(a.currentTime || 0);
    a.onended = () => { setIsPlaying(false); setCurrentTime(0); };
    a.onerror = () => { stop(); };
    setVoice(vc); setLangKey(key); setCurrentUrl(url);
    setCurrentTime(0); setDuration(0); setIsPlaying(true);
    a.play().then(() => setIsPlaying(true)).catch(() => { /* autoplay/erro: mantém UI */ });
  }, [teardown, stop]);

  // Toca o preview de uma voz. opts: { language, accent, previewUrl, key }.
  // Sem opts -> preview principal (idioma primário). Mesmo voice+key tocando -> toggle.
  const play = useCallback((vc, opts = {}) => {
    if (!vc) return;
    const entries = previewLanguagesOf(vc);
    let { language, accent, previewUrl, key } = opts;

    if (!key) {
      const primaryLang = (vc.voice_info && vc.voice_info.language) || '';
      const primary = entries.find((e) => (e.language || '').toLowerCase() === primaryLang.toLowerCase()) || entries[0];
      if (primary) {
        key = keyOf(primary.language, primary.accent);
        previewUrl = previewUrl || primary.preview_url;
      } else {
        key = '__main__';
        previewUrl = previewUrl || getPreviewUrl(vc);
      }
    }
    if (!previewUrl) {
      const hit = entries.find((e) => keyOf(e.language, e.accent) === key);
      previewUrl = (hit && hit.preview_url) || getPreviewUrl(vc);
    }

    // Toggle: mesma voz + mesmo idioma já no player.
    if (audioRef.current && voice && voice.id === vc.id && langKey === key) {
      if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
      else { audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {}); }
      return;
    }
    if (!previewUrl) return;
    start(vc, key, previewUrl);
  }, [voice, langKey, isPlaying, start]);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) { a.pause(); setIsPlaying(false); }
    else { a.play().then(() => setIsPlaying(true)).catch(() => {}); }
  }, [isPlaying]);

  const seek = useCallback((sec) => {
    const a = audioRef.current;
    if (a && Number.isFinite(sec)) {
      const max = Number.isFinite(a.duration) ? a.duration : sec;
      a.currentTime = Math.max(0, Math.min(sec, max));
      setCurrentTime(a.currentTime);
    }
  }, []);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (a) {
      const max = Number.isFinite(a.duration) ? a.duration : (a.currentTime || 0) + delta;
      a.currentTime = Math.max(0, Math.min((a.currentTime || 0) + delta, max));
      setCurrentTime(a.currentTime);
    }
  }, []);

  // Troca o idioma do preview da voz ATUAL (recarrega o áudio daquele idioma).
  const setLanguage = useCallback((entry) => {
    if (!voice || !entry || !entry.preview_url) return;
    start(voice, keyOf(entry.language, entry.accent), entry.preview_url);
  }, [voice, start]);

  // Registro/atualização do player EXTERNO (áudio de resultado da página).
  const showExternal = useCallback((desc, controls) => {
    teardown(); // para o preview interno
    setVoice(null); setLangKey(null); setCurrentUrl(null);
    setIsPlaying(false); setCurrentTime(0); setDuration(0);
    extCtl.current = controls || {};
    setExt(desc || null);
  }, [teardown]);
  const updateExternal = useCallback((patch) => {
    setExt((e) => (e ? { ...e, ...patch } : e));
  }, []);
  const clearExternal = useCallback(() => { extCtl.current = {}; setExt(null); }, []);

  useEffect(() => () => teardown(), [teardown]);

  const isResult = !!ext;
  const value = {
    // preview internals (cards + handlers da página)
    voice, langKey, play, setLanguage, stop,
    isVoicePlaying: (id) => isPlaying && !!voice && voice.id === id,
    activeKeyFor: (id) => (voice && voice.id === id ? langKey : null),
    nowPlaying: voice ? { voiceId: voice.id, langKey, isPlaying } : null,
    // registro do player EXTERNO (resultado) pela página
    showExternal, updateExternal, clearExternal,
    // VIEW unificada da barra inferior (preview OU resultado)
    barActive: isResult || !!voice,
    barKind: isResult ? 'result' : 'preview',
    barVoice: isResult ? null : voice,
    barLangKey: langKey,
    barName: isResult ? (ext.name || '') : (voice ? (voice.name || (voice.voice_info && voice.voice_info.name) || '') : ''),
    barCurrentUrl: isResult ? ext.url : currentUrl,
    barIsPlaying: isResult ? !!ext.isPlaying : isPlaying,
    barCurrentTime: isResult ? (ext.currentTime || 0) : currentTime,
    barDuration: isResult ? (ext.duration || 0) : duration,
    barToggle: () => { if (isResult) { if (extCtl.current.toggle) extCtl.current.toggle(); } else toggle(); },
    barSeek: (s) => { if (isResult) { if (extCtl.current.seek) extCtl.current.seek(s); } else seek(s); },
    barSkip: (d) => { if (isResult) { if (extCtl.current.skip) extCtl.current.skip(d); } else skip(d); },
    barStop: () => { if (isResult) { if (extCtl.current.stop) extCtl.current.stop(); clearExternal(); } else stop(); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
